import React, { useEffect, useRef, useState } from "react";
import { CostComplianceLabels as Labels } from "../cost-compliance/CostComplianceConstants";
import {
  CheckPicker,
  Loader,
  SelectPicker,
  Toggle,
  Tooltip,
  VStack,
  Whisper,
} from "rsuite";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  useComplianceFilters,
  useCostComplianceData,
  useDownloadCostComplianceData,
} from "../../../react-query/hooks/HCompliance";
import { numberFormatterInK } from "../../../utils/formatter";
import { sortCommodityFirst, getDefaultCommodity } from "../../../utils/helpers/commodityOrder";
import DangerArrowUpSm from "../../../assets/up-arrow-sm-danger.svg";
import useResponsiveWidth from "../../../react-query/hooks/useResponsiveWidth";
import DownloadIcon from "../../../assets/download-green.svg";
import { CSVDownload, CSVLink } from "react-csv";
import { set } from "rsuite/esm/internals/utils/date";
import ProductInfoIcon from "../../../assets/info-circle-grey.svg";
import { appInsights } from "../../../router/router";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export const EmptyMessageTemplate = () => {
  return <div className="text-center p-4">No Data available.</div>;
};

const CostCompliance = () => {
  const width = useResponsiveWidth();
  const csvRef: any = useRef(null);
  const [commodity, setCommodity] = useState<string>("");
  const [excelEnabled, setExcelEnabled] = useState(false);
  const [district, setDistrict] = useState<any>([]);
  const [vendor, setVendor] = useState<any>([]);
  const [month, setMonth] = useState<any>([]);
  const [filters, setFilters] = useState<any>({
    commodity: "",
    district: [],
    vendor: [],
    month: [],
  });
  const [checked, setChecked] = useState<boolean>(false);
  const [excelDownloadFilters, setExcelDownloadFilters] = useState<any>(null);
  const { complianceFilters, filtersLoaded } = useComplianceFilters(
    localStorage.getItem("userEmail"),
    "cost",
    commodity
  );
  const { complianceData, isLoading } = useCostComplianceData(
    checked ? "district" : "vendor",
    filters?.commodity,
    filters?.vendor,
    filters?.district && filters.district.length !== 0
      ? filters.district
      : complianceFilters && complianceFilters.districts
      ? complianceFilters.districts.map((ele: any) => ele.district)
      : [],
    filters?.month
  );

  const { excelData } = useDownloadCostComplianceData(
    filters?.commodity,
    filters?.month,
    excelDownloadFilters?.vendor,
    excelDownloadFilters?.district && excelDownloadFilters.district !== ""
      ? excelDownloadFilters?.district
      : complianceFilters && complianceFilters.districts
      ? complianceFilters.districts.map((ele: any) => ele.district)
      : [],
    excelEnabled
  );

  const downloadExcel = async (data: any) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(`Overpaid PO details_${commodity}`);
    const headers = [
      { header: "START_OF_WEEK", key: "START_OF_WEEK", width: 10 },
      { header: "COMMODITY", key: "COMMODITY", width: 10 },
      { header: "DISTRICT", key: "DISTRICT", width: 10 },
      { header: "PLANT_WERKS", key: "PLANT_WERKS", width: 10 },
      { header: "SUPPLIER", key: "VENDOR", width: 10 },
      { header: "SHORT_TEXT", key: "SHORT_TEXT", width: 10 },
      {
        header: "PURCHASING_DOCUMENT_EBELN",
        key: "PURCHASING_DOCUMENT_EBELN",
        width: 10,
        style: { numFmt: "0" },
      },
      {
        header: "MATERIAL_MATNR",
        key: "MATERIAL_MATNR",
        width: 10,
        style: { numFmt: "0" },
      },
      {
        header: "ADJUSTED_ORDER_QUANTITY",
        key: "ADJUSTED_ORDER_QUANTITY",
        width: 10,
      },
      {
        header: "PO_COST",
        key: "PO_COST",
        width: 10,
        style: { numFmt: "$#,##0.00" },
      },
      {
        header: "QUOTE",
        key: "QUOTE",
        width: 10,
        style: { numFmt: "$#,##0.00" },
      },
      {
        header: "OVERPAYMENT_VARIANCE",
        key: "OVERPAYMENT_VARIANCE",
        width: 10,
        style: { numFmt: "$#,##0.00" },
      },
    ];
    worksheet.columns = [...headers];
    const rows = [
      ...data?.map((row: any) => ({
        ...row,
        START_OF_WEEK: new Date(row.START_OF_WEEK).toLocaleDateString("en-us"),
        PO_COST: row.PO_COST.toLocaleString("en-us", {
          style: "currency",
          currency: "USD",
        }),
        QUOTE: row.QUOTE.toLocaleString("en-us", {
          style: "currency",
          currency: "USD",
        }),
        OVERPAYMENT_VARIANCE: row.OVERPAYMENT_VARIANCE.toLocaleString("en-us", {
          style: "currency",
          currency: "USD",
        }),
      })),
    ];
    rows.forEach((ele) => {
      let row: any = {
        START_OF_WEEK: ele["START_OF_WEEK"],
        COMMODITY: ele["COMMODITY"],
        DISTRICT: ele["DISTRICT"],
        PLANT_WERKS: ele["PLANT_WERKS"],
        VENDOR: ele["VENDOR"],
        SHORT_TEXT: ele["SHORT_TEXT"],
        PURCHASING_DOCUMENT_EBELN: Number(ele["PURCHASING_DOCUMENT_EBELN"]),
        MATERIAL_MATNR: Number(ele["MATERIAL_MATNR"]),
        ADJUSTED_ORDER_QUANTITY: ele["ADJUSTED_ORDER_QUANTITY"],
        PO_COST: parseFloat(ele["PO_COST"].slice(1).replace(/,/g, "")),
        QUOTE: parseFloat(ele["QUOTE"].slice(1).replace(/,/g, "")),
        OVERPAYMENT_VARIANCE: parseFloat(
          ele["OVERPAYMENT_VARIANCE"].slice(1).replace(/,/g, "")
        ),
      };

      worksheet.addRow(row);
    });
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.alignment = {
        wrapText: true,
        vertical: "middle",
        horizontal: "center",
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      cell.font = { bold: true };
    });

    // Apply styling to all data rows
    worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      if (rowNumber > 1) {
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.alignment = {
            wrapText: true,
            vertical: "middle",
            horizontal: "center",
          };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      }
    });
    worksheet.columns.forEach((column: any) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell: any) => {
        if (cell.value) {
          const cellValue = cell.value.toString();
          maxLength = Math.max(maxLength, cellValue.length);
        }
      });
      column.width = maxLength + 4; // Add some padding to the width
    });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Overpaid PO details_${commodity}.xlsx`);
    appInsights.trackEvent({
      name: `Compliance Monitoring: Cost Compliance`,
      properties: {
        description: `${localStorage.getItem(
          "userEmail"
        )} has downloaded "Overpaid PO details_${commodity}" File`,
      },
    });
  };

  useEffect(() => {
    if (excelData && excelData?.length > 0 && excelEnabled) {
      // csvRef?.current.link.click();
      downloadExcel(excelData);
      setExcelEnabled(false);
    }
  }, [excelData]);

  const handleExcelDownload = (data: any, type: "All" | "One") => {
    if (checked) {
      const districtList =
        type === "All" && data.map((item: any) => item.district);
      setExcelDownloadFilters({
        district:
          type === "All"
            ? districtList.filter(
                (district: string, index: number) =>
                  districtList.indexOf(district) === index
              )
            : [data?.district],
        vendor: filters?.vendor,
      });
    } else {
      const vendorList = type === "All" && data.map((item: any) => item.vendor);
      setExcelDownloadFilters({
        district: filters?.district,
        vendor:
          type === "All"
            ? vendorList.filter(
                (vendor: string, index: number) =>
                  vendorList.indexOf(vendor) === index
              )
            : [data?.vendor],
      });
    }
    setExcelEnabled(true);
  };

  useEffect(() => {
    if (
      !filtersLoaded &&
      Object?.keys(complianceFilters)?.length > 0 &&
      filters?.commodity === ""
    ) {
      setCommodity(getDefaultCommodity(complianceFilters?.commodity));
      setMonth([complianceFilters?.dates[0]?.["start_of_week"]]);
      setFilters({
        commodity: getDefaultCommodity(complianceFilters?.commodity),
        month: [complianceFilters?.dates[0]?.["start_of_week"]],
      });
    }
  }, [filtersLoaded]);

  // let formattedData: any = excelData?.map((row) => ({
  //   ...row,
  //   START_OF_WEEK: new Date(row.START_OF_WEEK).toLocaleDateString("en-us"),
  //   PO_COST: row.PO_COST.toLocaleString("en-us", {
  //     style: "currency",
  //     currency: "USD",
  //   }),
  //   QUOTE: row.QUOTE.toLocaleString("en-us", {
  //     style: "currency",
  //     currency: "USD",
  //   }),
  //   OVERPAYMENT_VARIANCE: row.OVERPAYMENT_VARIANCE.toLocaleString("en-us", {
  //     style: "currency",
  //     currency: "USD",
  //   }),
  // }));

  return (
    <div className="cost-compliance-container flex flex-col justify-stretch items-stretch h-full volume-allocation">
      {(isLoading || filtersLoaded) && (
        <Loader
          backdrop
          content="Loading..."
          size="md"
          vertical
          className="text-black font-bold"
          style={{ opacity: 0.6, zIndex: 9999 }}
        />
      )}
      <div className="grow shrink-0 basis-[17%] ps-[3%] pr-[5%] pt-[1.6%] flex flex-col flex-nowrap justify-start items-start gap-0 shadow-normal relative">
        <div className="flex justify-between items-center w-full">
          <div className="flex flex-col w-full">
            <div className="font-normal text-[2vw]">
              {Labels.costComplianceLabel}
            </div>
            <div className="flex items-center">
              <span className="font-bold text-fs16 text-[#858585]">
                Purchase data last refreshed:{" "}
              </span>
              <span className="font-bold underline text-fs16 text-deloitteGreen pl-[0.25vh]">
                {complianceData?.refreshed_date}
              </span>
            </div>
          </div>
          <div className="flex justify-start items-center gap-8">
            <div className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]">
              <label className="font-bold text-fs16">
                {Labels.commodityFilterLabel}
              </label>
              <SelectPicker
                className="dropdown-responsive-width"
                data={sortCommodityFirst(complianceFilters?.commodity)}
                value={commodity}
                labelKey="commodity"
                valueKey="commodity"
                style={{ width: width }}
                onChange={(value: string | null) => {
                  if (value) {
                    setCommodity(value);
                    setFilters({
                      ...filters,
                      commodity: value,
                      vendor: null,
                    });
                    setVendor(null);
                  }
                }}
                placeholder="All"
                cleanable={false}
              ></SelectPicker>
            </div>
            <div className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]">
              <label className="font-bold text-fs16">
                {Labels.districtFilterLabel}
              </label>
              <VStack>
                <CheckPicker
                  sticky
                  className="dropdown-responsive-width"
                  countable={true}
                  data={complianceFilters?.districts ?? []}
                  preventOverflow={true}
                  style={{ width: width }}
                  onClean={() => {
                    setFilters({
                      ...filters,
                      district: null,
                    });
                  }}
                  onChange={(value: string[]) => {
                    setDistrict(value.length > 0 ? value : null);
                  }}
                  onClose={() => {
                    setFilters({
                      commodity,
                      vendor,
                      district,
                      month,
                    });
                  }}
                  cleanable={true}
                  value={district ?? []}
                  placeholder="All"
                  labelKey="district"
                  valueKey="district"
                  menuMaxHeight={150}
                />
              </VStack>
            </div>
            <div className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]">
              <label className="font-bold text-fs16">
                {Labels.vendorFilterLabel}
              </label>
              <CheckPicker
                sticky
                className="dropdown-responsive-width"
                countable={true}
                style={{ width: width }}
                data={complianceFilters?.vendor ?? []}
                preventOverflow={true}
                onChange={(value: string[]) =>
                  setVendor(value.length > 0 ? value : null)
                }
                onClose={() => {
                  setFilters({
                    commodity,
                    vendor,
                    district,
                    month,
                  });
                }}
                onClean={() => {
                  setFilters({
                    ...filters,
                    vendor: null,
                  });
                }}
                cleanable={true}
                value={vendor ?? []}
                placeholder="All"
                labelKey="vendor"
                valueKey="vendor"
                menuMaxHeight={150}
              />
            </div>
            <div className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]">
              <label className="font-bold text-fs16">
                {Labels.monthFilterLabel}
              </label>
              <CheckPicker
                className="dropdown-responsive-width"
                data={complianceFilters?.dates ?? []}
                value={month ?? []}
                countable={false}
                style={{ width: width }}
                labelKey="start_of_week"
                valueKey="start_of_week"
                onChange={(value: string[]) => {
                  setMonth(value?.length > 0 ? value : null);
                }}
                onClose={() => {
                  setFilters({
                    commodity,
                    vendor,
                    district,
                    month,
                  });
                }}
                onClean={() => {
                  setFilters({
                    ...filters,
                    month: null,
                  });
                }}
                placeholder="All"
                menuMaxHeight={150}
                cleanable={true}
              ></CheckPicker>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#F1F1F1] grow shrink p-[2%] pb-[0.4%] pr-[5%] flex flex-col flex-nowrap justify-start items-start gap-6 h-full w-full">
        <div className="flex flex-row gap-0 justify-between items-center basis-[10%] shrink-0 w-full h-full bg-white p-pd13 shadow-module-card border border-[#bebebe] rounded-md">
          <div className="flex flex-col justify-center">
            <div className="font-bold text-[1.8vh]">
              Total Overpayments
              <span>
                <Whisper
                  className="inline"
                  speaker={
                    <Tooltip placement="auto">
                      The incremental amount paid to suppliers due to the PO
                      price being higher than the quoted price. Please review
                      these cases.
                    </Tooltip>
                  }
                >
                  {" "}
                  <img
                    src={ProductInfoIcon}
                    alt="info"
                    className="w-[1.75vh] cursor-pointer inline"
                  ></img>
                </Whisper>
              </span>
            </div>
           
          </div>
          <div className="text-[4.8vh] pr-2">
            {numberFormatterInK(
              complianceData["total_overpaid_amount"],
              1,
              "$"
            )}
          </div>
        </div>
        <div className="flex flex-row gap-4 justify-between items-center basis-[5%] shrink-0 w-full bg-white py-3 px-2 shadow-module-card border border-[#bebebe] rounded-md">
          <div className="font-bold text-[2vh] flex justify-between items-center gap-2">
            <span>Overpayments</span>
            {!isLoading &&
              complianceData["cost_compliance_data"]?.filter(
                (ele: any) => ele.overpayment_variance
              ).length !== 0 && (
                <>
                  <Whisper
                    placement="left"
                    speaker={
                      <Tooltip placement="auto">Overpaid PO details</Tooltip>
                    }
                  >
                    <div>
                      <span
                        className="cursor-pointer"
                        onClick={() => {
                          // console.log(
                          //   complianceData,
                          //   complianceData["cost_compliance_data"],
                          //   complianceData["cost_compliance_data"]?.filter(
                          //     (ele: any) => ele.overpayment_variance
                          //   )
                          // );
                          handleExcelDownload(
                            [
                              ...complianceData["cost_compliance_data"]?.filter(
                                (ele: any) => ele.overpayment_variance
                              ),
                            ],
                            "All"
                          );
                        }}
                      >
                        <img src={DownloadIcon} className="w-[1vw] h-[1vw]" />
                      </span>
                    </div>
                  </Whisper>
                </>
              )}
          </div>
          <div className="text-fs12">
            <span className={checked === false ? "font-bold" : ""}>
              By Supplier
            </span>
            <Toggle
              checked={checked}
              onChange={(value) => {
                setChecked(!checked);
              }}
              className="px-2"
              color="green"
            />
            <span className={checked === true ? "font-bold" : ""}>
              By District
            </span>
          </div>
        </div>
        {!isLoading && !filtersLoaded && (
          <div className="basis-[80%] h-full w-full  justify-start items-center cost-compliance-table">
            <DataTable
              sortField="district"
              sortOrder={1}
              removableSort
              value={!isLoading && complianceData["cost_compliance_data"]}
              stripedRows
              emptyMessage={EmptyMessageTemplate}
              scrollable
              className="shadow-module-card"
              scrollHeight="50.25vh"
              showGridlines
            >
              <Column
                field={checked ? "district" : "vendor"}
                style={{ width: "200px" }}
                header={checked ? "District" : "Supplier"}
                sortable
                body={(rowdata) => {
                  return (
                    <div className="flex items-center justify-between w-full">
                      <span className={`flex flex-nowrap gap-1`}>
                        {rowdata[checked ? "district" : "vendor"]}
                      </span>
                      {rowdata?.overpayment_variance && (
                        <>
                          <Whisper
                            placement="left"
                            speaker={
                              <Tooltip placement="auto">
                                Overpaid PO details
                              </Tooltip>
                            }
                          >
                            <div>
                              <span
                                className="cursor-pointer"
                                onClick={() => {
                                  handleExcelDownload(rowdata, "One");
                                }}
                              >
                                <img
                                  src={DownloadIcon}
                                  className="w-[1vw] h-[1vw]"
                                />
                              </span>
                              {/* <CSVLink
                                filename={`Overpaid PO details_${commodity}`}
                                target="_blank"
                                data={excelData ? formattedData : [{}]}
                                ref={csvRef}
                                onClick={() => {
                                  appInsights.trackEvent({
                                    name: `Compliance Monitoring: Cost Compliance`,
                                    properties: {
                                      description: `${localStorage.getItem(
                                        "userEmail"
                                      )} has downloaded "Overpaid PO details_${commodity}" File`,
                                    },
                                  });
                                }}
                              /> */}
                            </div>
                          </Whisper>
                        </>
                      )}
                    </div>
                  );
                }}
              ></Column>

              <Column
                sortable
                field="actual_spend_amount"
                header={<div className="flex w-full">Actual Spend Amount</div>}
                body={(rowdata) => {
                  return (
                    <span>
                      {rowdata?.actual_spend_amount
                        ? numberFormatterInK(
                            rowdata.actual_spend_amount,
                            1,
                            "$"
                          )
                        : "-"}
                    </span>
                  );
                }}
              ></Column>
              <Column
                sortable
                field="planned_spend_amount"
                header={<div className="flex w-full">Planned Spend Amount</div>}
                body={(rowdata) => {
                  return (
                    <span>
                      {rowdata?.planned_spend_amount
                        ? numberFormatterInK(
                            rowdata?.planned_spend_amount,
                            1,
                            "$"
                          )
                        : "-"}
                    </span>
                  );
                }}
              ></Column>
              <Column
                sortable
                field="overpayment_variance"
                header={
                  <div className="flex w-[95%] justify-start gap-1">
                    Overpayment Variance{" "}
                    <span>
                      <Whisper
                        placement="top"
                        className="inline"
                        speaker={
                          <Tooltip placement="auto">
                            The incremental amount paid to suppliers due to the
                            PO price being higher than the quoted price. Please
                            review these cases.
                          </Tooltip>
                        }
                      >
                        {" "}
                        <img
                          src={ProductInfoIcon}
                          alt="info"
                          className="w-[1.75vh] cursor-pointer inline"
                        ></img>
                      </Whisper>
                    </span>
                  </div>
                }
                body={(rowdata) => {
                  return (
                    <span>
                      {rowdata?.overpayment_variance
                        ? numberFormatterInK(
                            rowdata.overpayment_variance,
                            1,
                            "$"
                          )
                        : "-"}
                    </span>
                  );
                }}
              ></Column>

              <Column
                sortable
                field="overpayment_variance_percentage"
                header={
                  <div className="flex justify-between w-full">
                    Overpayment Variance %
                  </div>
                }
                body={(rowdata) => {
                  return (
                    <div className="flex items-center justify-between w-full">
                      <span
                        className={`flex flex-nowrap gap-1 ${
                          rowdata.overpayment_variance_percentage > 0
                            ? "text-alert"
                            : "text-deloitteGreen"
                        }`}
                      >
                        {rowdata.overpayment_variance_percentage === 0
                          ? "-"
                          : Math.abs(
                              rowdata.overpayment_variance_percentage.toFixed(1)
                            ) + "%"}
                        {rowdata.overpayment_variance_percentage > 0 && (
                          <img
                            width={"80%"}
                            src={DangerArrowUpSm}
                            alt="danger"
                          ></img>
                        )}
                      </span>
                    </div>
                  );
                }}
              ></Column>
            </DataTable>
          </div>
        )}
      </div>
    </div>
  );
};

export default CostCompliance;
